/**
 * @module utils/originalDate
 * @description Utilities for handling original publication date in the Extra field
 *
 * Zotero doesn't have a native "original date" field, so we store it in the
 * Extra field using the format: "original-date: YYYY" or "original-date: YYYY-MM-DD"
 *
 * This is particularly useful for:
 * - Reprints and new editions of classic works
 * - Translations published years after the original
 * - Collected works with original publication dates
 */

/**
 * Extract original date from the Extra field
 *
 * @param extraText - The content of the Extra field
 * @returns The original date string, or empty string if not found
 *
 * @example
 * extractOriginalDate("original-date: 1859\nSome other note")
 * // Returns: "1859"
 */
export function extractOriginalDate(extraText: string | undefined): string {
  if (!extraText) return '';
  const match = extraText.match(/^original-date:\s*(.+)$/m);
  return match ? match[1].trim() : '';
}

/**
 * Update the Extra field with a new original date
 *
 * If an original date already exists, it will be replaced.
 * If the new date is empty, any existing original-date line will be removed.
 *
 * @param extraText - The current content of the Extra field
 * @param originalDate - The new original date to set
 * @returns The updated Extra field content
 *
 * @example
 * updateExtraWithOriginalDate("Some note", "1859")
 * // Returns: "original-date: 1859\nSome note"
 *
 * updateExtraWithOriginalDate("original-date: 1850\nNote", "1859")
 * // Returns: "original-date: 1859\nNote"
 *
 * updateExtraWithOriginalDate("original-date: 1850\nNote", "")
 * // Returns: "Note"
 */
export function updateExtraWithOriginalDate(
  extraText: string | undefined,
  originalDate: string
): string {
  // Remove any existing original-date line
  let updatedExtra = (extraText || '').replace(/^original-date:\s*.+\n?/m, '').trim();

  // Add the new original date at the beginning if provided
  if (originalDate && originalDate.trim()) {
    const newLine = `original-date: ${originalDate.trim()}`;
    updatedExtra = updatedExtra ? `${newLine}\n${updatedExtra}` : newLine;
  }

  return updatedExtra;
}

/**
 * Get the Extra field content without the original-date line
 *
 * Useful for displaying the Extra field in read mode without duplicating
 * the original date that's shown in its own field.
 *
 * @param extraText - The content of the Extra field
 * @returns The Extra content without the original-date line
 *
 * @example
 * cleanExtraForDisplay("original-date: 1859\nTranslated by John Doe")
 * // Returns: "Translated by John Doe"
 */
export function cleanExtraForDisplay(extraText: string | undefined): string {
  if (!extraText) return '';
  return extraText.replace(/^original-date:\s*.+\n?/m, '').trim();
}

/**
 * Check if an Extra field contains an original date
 *
 * @param extraText - The content of the Extra field
 * @returns True if an original date is present
 */
export function hasOriginalDate(extraText: string | undefined): boolean {
  return extractOriginalDate(extraText) !== '';
}

/**
 * Validate a date string format
 *
 * Accepts various formats:
 * - YYYY (e.g., "1859")
 * - YYYY-MM (e.g., "1859-11")
 * - YYYY-MM-DD (e.g., "1859-11-24")
 * - Month YYYY (e.g., "November 1859")
 * - Free text dates (e.g., "circa 1860", "early 19th century")
 *
 * @param dateStr - The date string to validate
 * @returns True if the date format is valid
 */
export function isValidDateFormat(dateStr: string): boolean {
  if (!dateStr || !dateStr.trim()) return false;

  // Accept any non-empty string as Zotero is flexible with dates
  // But we can do basic validation for common formats
  const trimmed = dateStr.trim();

  // Check for year-only format (1800-2100)
  if (/^\d{4}$/.test(trimmed)) {
    const year = parseInt(trimmed, 10);
    return year >= 1000 && year <= 2100;
  }

  // Check for YYYY-MM format
  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    const [year, month] = trimmed.split('-').map(Number);
    return year >= 1000 && year <= 2100 && month >= 1 && month <= 12;
  }

  // Check for YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const date = new Date(trimmed);
    return !isNaN(date.getTime());
  }

  // Accept any other non-empty string (free text dates)
  return trimmed.length > 0;
}
